import React from "react";
import { callApi, validateToken } from "../../lib/utils";
import { withRouter, BrowserRouter as Router, Route } from "react-router-dom";
import { connect } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { css } from "aphrodite";
import SideDetails from "../GenericComponents/SideDetails";
import SideMenuComponentsHeader from "../GenericComponents/Headers/SideMenuComponentsHeader";
import CustomLoader from "../GenericComponents/CustomLoader";
import CustomModal from "../GenericComponents/CustomModal";
import Footer from "../GenericComponents/Footer";
import styles from "./style";
import AbnormalMotion from "./AbnormalMotion";
import HRMSettings from "./HRMSettings";
import FallDetectionSettings from "./FallDetectionSettings";
import CallForHelpSettings from "./CallForHelpSettings";
import "./DiseaseMonitoringSettings.css";

class DiseaseMonitoringSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      abnormalmotionDefaultSettings: {},
      hrmDefaultSettings: {},
      fallDetectionDefaultSettings: {},
      callForHelp:{},
      loading: false,
      customModal: false,
      settingsTitle: ""
    };
  }

  componentDidMount = () => {
    this.initializeDefaultSettings();
  };

  componentDidUpdate = (prevProps) => {
    // The master settings list loads in parallel with the dashboard's other
    // calls (async.parallel), so it may not be in Redux yet when this page
    // mounts. Initialize once it arrives so the default maps aren't left empty.
    const hadMaster = (prevProps.mobileAppSettingsMasterList?.length ?? 0) > 0;
    const hasMaster = (this.props.mobileAppSettingsMasterList?.length ?? 0) > 0;
    if (!hadMaster && hasMaster) {
      this.initializeDefaultSettings();
    }
  };

  initializeDefaultSettings = () => {
    // Master defaults aren't loaded yet; componentDidUpdate will retry when
    // mobileAppSettingsMasterList arrives.
    if (!this.props.mobileAppSettingsMasterList?.length) {
      return;
    }

    let abnormalmotionDefaultSettings = {};
    let hrmDefaultSettings = {};
    let fallDetectionDefaultSettings = {};
    let callForHelpDefaultSettings = {};

    this.props.mobileAppSettingsMasterList.filter((val) => { return val.eventType === 1 }).map((settings) => {
      return (abnormalmotionDefaultSettings[settings["code"]] = settings);
    });
    this.props.mobileAppSettingsMasterList.filter((val) => { return val.eventType === 18 }).map((settings) => {
      return (hrmDefaultSettings[settings["code"]] = settings);
    });
    this.props.mobileAppSettingsMasterList.filter((val) => { return val.eventType === 28 }).map((settings) => {
      return (fallDetectionDefaultSettings[settings["code"]] = settings);
    });
    this.props.mobileAppSettingsMasterList.filter((settings) => settings.code === "ENABLE_CALL_FOR_HELP").map((settings) => {
      return (callForHelpDefaultSettings[settings["code"]] = settings);
    });

    // Set the default maps first, then fetch — the createObjectFor* helpers
    // read these maps from state to fill in any fields the API omits.
    this.setState(
      { abnormalmotionDefaultSettings, hrmDefaultSettings, fallDetectionDefaultSettings, callForHelpDefaultSettings },
      () => {
        validateToken((err) => {
          if (err) {
            window.location = "/logout";
          }
          this.fetchSettings();
        });
      }
    );
  };

  fetchSettings = () => {
    this.setState({ loading: true });
    callApi(`/api/web/patient/settings/fetch`, {}, "GET")
      .then((response) => {
        //console.log("RES...", response);
        let abnormalMotionSettings = {};
        let hrmSettings = {};
        let fallDetectionSettings = {};
        let callForHelpSettings = {};

        // A patient only receives the blocks for modules they're provisioned
        // for, so some of SD/HRM/FD/CALRT are routinely absent. Build each
        // section independently so an unexpected failure in one can never abort
        // the others — that's what previously left a fully-valid SD block
        // undefined on screen when a sibling block (HRM/FD) was missing.
        const result = response.status === 200 ? response.data.data.settings : {};
        const build = (fn) => {
          try {
            return fn(result);
          } catch (e) {
            console.log("settings build error: ", e);
            return fn({});
          }
        };
        abnormalMotionSettings = build(this.createObjectForAMSettings);
        hrmSettings = build(this.createObjectForHRMSettings);
        fallDetectionSettings = build(this.createObjectForFallDetectionSettings);
        callForHelpSettings = build(this.createObjectForCallForHelpSettings);

        this.setState({ loading: false }, () => {
          this.setState({ callForHelp: callForHelpSettings });
          this.props.setAbnormalSettingsData(abnormalMotionSettings);
          this.props.setHRMSettingsData(hrmSettings);
          this.props.setFallDetectionSettingsData(fallDetectionSettings);
          this.props.setCallForHelpSettingsData(callForHelpSettings);
        });
      })
      .catch((err) => {
        console.log("err: ", err);
        this.setState({ loading: false });
      });
  };

  createObjectForAMSettings = (settings) => {
    const d = this.state.abnormalmotionDefaultSettings;
    // No saved record (new patient / RECORD_NOT_FOUND) → seed full defaults so
    // the form is usable. `?.` guards keep this from throwing if a master key
    // is missing. When a record exists we use its values verbatim and do NOT
    // backfill omitted fields — per business, an unset field stays unset
    // (rendered as "Not set"), it is not silently defaulted.
    if (!settings?.SD) {
      return {
        dayTimeStart: d.DAY_TIME_START?.defaultValue,
        dayTimeEnd: d.DAY_TIME_END?.defaultValue,
        nightTimeStart: d.NIGHT_TIME_START?.defaultValue,
        nightTimeEnd: d.NIGHT_TIME_END?.defaultValue,
        dayTimeSensitivity: parseInt(d.DAY_SENSITIVITY?.defaultValue),
        nightTimeSensitivity: parseInt(d.NIGHT_SENSITIVITY?.defaultValue),
        dayTimeDuration: parseInt(d.DAY_DURATION?.defaultValue),
        nightTimeDuration: parseInt(d.NIGHT_DURATION?.defaultValue),
        enableAudioRecording: d.AUDIO_RECORDING?.defaultValue === "true",
        audioRecordingDuration: parseInt(d.AUDIO_RECORD_DURATION?.defaultValue),
        minHRThreshold: 40,
        maxHRThreshold: 120,
        hrWithTriggerEvent: 5,
        hrAlarmTone: "alert_ring_tone.mp3",
        allowSettingChangeOnSmartWatch: d.ALLOW_SETTING_CHANGE_ON_SMART_WATCH?.defaultValue === "true",
        allowSnoozeOnSmartWatch: d.ALLOW_SNOOZE_ON_SMART_WATCH?.defaultValue === "true",
        enableAbnormalMotionAlarmRingTone: d.ENABLE_ABNORMAL_MOTION_ALARM_RINGTONE?.defaultValue === "true",
        deviceForAlarmRingTone: d.DEVICE_FOR_ALARM_RINGTONE?.defaultValue,
        enableMotionMonitoring: d.ENABLE_MOTION_MONITORING?.defaultValue === "true",
      };
    }

    const sd = settings.SD;
    return {
      dayTimeStart: sd.dayTimeStart,
      dayTimeEnd: sd.dayTimeEnd,
      nightTimeStart: sd.nightTimeStart,
      nightTimeEnd: sd.nightTimeEnd,
      dayTimeSensitivity: sd.dayTimeSensitivity,
      nightTimeSensitivity: sd.nightTimeSensitivity,
      dayTimeDuration: sd.dayTimeDuration,
      nightTimeDuration: sd.nightTimeDuration,
      enableAudioRecording: sd.enableAudioRecording,
      audioRecordingDuration: sd.audioRecordingDuration,
      minHRThreshold: 40,
      maxHRThreshold: 120,
      hrWithTriggerEvent: 5,
      hrAlarmTone: "alert_ring_tone.mp3",
      allowSettingChangeOnSmartWatch: sd.allowSettingChangeOnSmartWatch,
      allowSnoozeOnSmartWatch: sd.allowSnoozeOnSmartWatch,
      enableAbnormalMotionAlarmRingTone: sd.enableAbnormalMotionAlarmRingTone,
      deviceForAlarmRingTone: sd.deviceForAlarmRingTone,
      enableMotionMonitoring: sd.enableMotionMonitoring,
    };
  };

  createObjectForHRMSettings = (settings) => {
    const d = this.state.hrmDefaultSettings;
    // No saved record → seed full defaults; existing record → verbatim, no
    // per-field backfill (see createObjectForAMSettings).
    if (!settings?.HRM) {
      return {
        lowHRThreshold: parseInt(d.LOW_HR_THRESHOLD?.defaultValue),
        highHRThreshold: parseInt(d.HIGH_HR_THRESHOLD?.defaultValue),
        lowHRThresholdDuration: parseInt(d.LOW_HR_THRESHOLD_DURATION?.defaultValue),
        highHRThresholdDuration: parseInt(d.HIGH_HR_THRESHOLD_DURATION?.defaultValue),
        hrThresholdDuration: parseInt(d.HR_THRESHOLD_DURATION?.defaultValue),
        hrAlarmTone: d.HR_ALARM_TONE?.defaultValue,
        allowSettingChangeOnSmartWatch: d.ALLOW_SETTING_CHANGE_ON_SMART_WATCH?.defaultValue === "true",
        allowSnoozeOnSmartWatch: d.ALLOW_SNOOZE_ON_SMART_WATCH?.defaultValue === "true",
        enableHRMAlarmRingtone: d.ENABLE_HRM_ALARM_RINGTONE?.defaultValue === "true",
        deviceForAlarmRingTone: d.DEVICE_FOR_ALARM_RINGTONE?.defaultValue,
        enableHRMonitoring: d.ENABLE_HR_MONITORING?.defaultValue === "true",
      };
    }

    const hrm = settings.HRM;
    return {
      lowHRThreshold: hrm.lowHRThreshold,
      highHRThreshold: hrm.highHRThreshold,
      lowHRThresholdDuration: hrm.lowHRThresholdDuration ?? parseInt(d.LOW_HR_THRESHOLD_DURATION?.defaultValue),
      highHRThresholdDuration: hrm.highHRThresholdDuration ?? parseInt(d.HIGH_HR_THRESHOLD_DURATION?.defaultValue),
      hrThresholdDuration: hrm.hrThresholdDuration,
      hrAlarmTone: hrm.hrAlarmTone,
      allowSettingChangeOnSmartWatch: hrm.allowSettingChangeOnSmartWatch,
      allowSnoozeOnSmartWatch: hrm.allowSnoozeOnSmartWatch,
      enableHRMAlarmRingtone: hrm.enableHRMAlarmRingtone,
      deviceForAlarmRingTone: hrm.deviceForAlarmRingTone,
      enableHRMonitoring: hrm.enableHRMonitoring,
    };
  };

  createObjectForFallDetectionSettings = (settings) => {
    const d = this.state.fallDetectionDefaultSettings;
    // No saved record → seed defaults; existing record → verbatim.
    if (!settings?.FD) {
      return {
        enableFallDetection: d.ENABLE_FALL_DETECTION?.defaultValue === "true",
        enableFallEventAlarmRingtone: d.ENABLE_FALL_EVENT_ALARM_RINGTONE?.defaultValue === "true",
      };
    }

    const fd = settings.FD;
    return {
      enableFallDetection: fd.enableFallDetection,
      enableFallEventAlarmRingtone: fd.enableFallEventAlarmRingtone,
    };
  };

  createObjectForCallForHelpSettings = (settings) => {
    return {
      enableCallForHelp:
        settings?.CALRT?.enableCallForHelp ??
        settings?.CAlRT?.enableCallForHelp ??
        settings?.SD?.enableCallForHelp ??
        settings?.SD?.allowMotionMonitoring ??
        settings?.HRM?.enableCallForHelp ??
        settings?.HRM?.allowHeartRateMonitoring ??
        (this.state.callForHelpDefaultSettings?.ENABLE_CALL_FOR_HELP?.defaultValue === "true"),
    };
  };

  updateCallForHelpSettings = (callForHelpSettings) => {
    this.setState({ loading: true });
    validateToken((err) => {
      if (err) {
        window.location = "/logout";
      }
      callApi(
        `/api/web/patient/settings/call-for-help/${this.props.profileData?.id}`,
        callForHelpSettings,
        "PUT"
      )
        .then((response) => {
          this.setState({ loading: false, settingsTitle: "Call For Help Settings updated successfully !", customModal: true });
        })
        .catch((err) => {
          console.log("err: ", err);
          this.setState({ loading: false });
        });
    });
  };

  updateAbnormalMotionSettings = (abnormalMotionSettings) => {
    //console.log("abnormalMotionSettings...", abnormalMotionSettings);
    this.setState({ loading: true });
    validateToken((err) => {
      if (err) {
        window.location = "/logout";
      }
      callApi(
        `/api/web/patient/settings/abnormal-motion`,
        abnormalMotionSettings,
        "PUT"
      )
        .then((response) => {
          this.setState({ loading: false, settingsTitle: "Motion Monitoring Settings updated successfully !", customModal: true });
          //this.props.history.push("/dashboard");
        })
        .catch((err) => {
          console.log("err: ", err);
          this.setState({ loading: false });
        });
    });
  };

  updateHRMSettings = (hrmSettings) => {
    this.setState({ loading: true });
    validateToken((err) => {
      if (err) {
        window.location = "/logout";
      }
      callApi(
        `/api/web/patient/settings/heart-rate/monitoring`,
        hrmSettings,
        "PUT"
      )
        .then((response) => {
          console.log("hrmSettings res...", response);
          this.setState({ loading: false, settingsTitle: "Heart Rate Monitoring Settings updated successfully !", customModal: true });
          //this.props.history.push("/dashboard");
        })
        .catch((err) => {
          console.log("err: ", err);
          this.setState({ loading: false });
        });
    });
  };

  updateFallDetectionSettings = (fallDetectionSettings) => {
    this.setState({ loading: true });
    validateToken((err) => {
      if (err) {
        window.location = "/logout";
      }
      callApi(
        `/api/web/patient/settings/fall-detection/monitoring`,
        fallDetectionSettings,
        "PUT"
      )
        .then((response) => {
          this.setState({ loading: false, settingsTitle: "Fall Detection Settings updated successfully !", customModal: true });
          //this.props.history.push("/dashboard");
        })
        .catch((err) => {
          console.log("err: ", err);
          this.setState({ loading: false });
        });
    });
  };

  closeSettings = () => {
    this.props.history.push("/dashboard");
  };

  handleModal = () => {
    this.setState({ customModal: !this.state.customModal });
  };

  render() {

    return (
      <container>
        <CustomModal
          header={""}
          name={""}
          size={"sm"}
          title={this.state.settingsTitle}
          show={this.state.customModal}
          confirmButtons={false}
          action={this.handleModal}
        />
        <CustomLoader loading={this.state.loading} />
        <SideMenuComponentsHeader title={"Monitoring Settings"} />
        <div>
          {" "}
          <Row className={`${css(styles.mainGrid)} monitoring-settings-page`}>
            <Col xs={12} sm={6} md={3} className={css(styles.sideStyle)}>
              <SideDetails />
            </Col>
            <Col xs={12} sm={6} md={9}>
              <Router>
                <Route path="/settings/abnormal-motion">
                  {Object.keys(this.state.abnormalmotionDefaultSettings).length !== 0 && (
                    <AbnormalMotion
                      setAbnormalSettingsData={
                        this.props.setAbnormalSettingsData
                      }
                      defaultSettings={this.state.abnormalmotionDefaultSettings}
                      updateAbnormalMotionSettings={
                        this.updateAbnormalMotionSettings
                      }
                      closeSettings={this.closeSettings}
                    />
                  )}
                </Route>
                <Route path="/settings/hrm">
                  {Object.keys(this.state.hrmDefaultSettings).length !== 0 && (
                    <HRMSettings
                      setHRMSettingsData={
                        this.props.setHRMSettingsData
                      }
                      defaultSettings={this.state.hrmDefaultSettings}
                      updateHRMSettings={
                        this.updateHRMSettings
                      }
                      closeSettings={this.closeSettings}
                    />
                  )}
                </Route>
                <Route path="/settings/fall-detection">
                  {Object.keys(this.state.fallDetectionDefaultSettings).length !== 0 && (
                    <FallDetectionSettings
                      setFallDetectionSettingsData={
                        this.props.setFallDetectionSettingsData
                      }
                      updateFallDetectionSettings={
                        this.updateFallDetectionSettings
                      }
                      closeSettings={this.closeSettings}
                    />
                  )}
                </Route>
                <Route path="/settings/call-for-help">
                  {Object.keys(this.props.callForHelpSettings || {}).length !== 0 && (
                    <CallForHelpSettings
                      setCallForHelpSettingsData={
                        this.props.setCallForHelpSettingsData
                      }
                      callForHelpSettings={this.props.callForHelpSettings}
                      patientDetail={this.props.profileData?.patientData}
                      updateCallForHelpSettings={
                        this.updateCallForHelpSettings
                      }
                      closeSettings={this.closeSettings}
                    />
                  )}
                </Route>
              </Router>
            </Col>
          </Row>
          <Footer />
        </div>
      </container>
    );
  }
}

export default connect(
  (store) => {
    return {
      mobileAppSettingsMasterList: store.dashboard.mobileAppSettingsMasterList,
      profileData: store.dashboard.profileData,
      callForHelpSettings: store.settings.callForHelpSettings,
    };
  },
  (dispatch) => {
    return {
      setAbnormalSettingsData: (abnormalMotionSettings) => {
        dispatch({
          type: "SET_ABNORMAL_MOTION_SETTINGS",
          payload: { abnormalMotionSettings },
        });
      },
      setHRMSettingsData: (hrmSettings) => {
        dispatch({
          type: "SET_HRM_SETTINGS",
          payload: { hrmSettings },
        });
      },
      setFallDetectionSettingsData: (fallDetectionSettings) => {
        dispatch({
          type: "SET_FALL_DETECTION_SETTINGS",
          payload: { fallDetectionSettings },
        });
      },
      setCallForHelpSettingsData: (callForHelpSettings) => {
        dispatch({
          type: "SET_CALL_FOR_HELP_SETTINGS",
          payload: { callForHelpSettings },
        });
      },
    };
  }
)(withRouter(DiseaseMonitoringSettings));
